// Чистая логика режима «разыграть с компом» (без DOM, покрыта vitest). Движок
// инжектится (DI) → тест на моке. Сила игры сознательно скромная и быстрая:
// depth 8 / movetime 600мс — играбельно на телефоне и без долгих раздумий.
import { Chess } from 'chess.js';
import { cpWhiteFromAnalysis, type EngineAnalysis } from '@ext/core/eval-utils';
import type { AnalyzeOptions } from '@ext/engine/engine';

/** Ручки движка для игрового режима (быстро и не слишком сильно). */
export const PLAY_OPTS: AnalyzeOptions = { depth: 8, multipv: 1, movetimeMs: 600 };

/** Задержка перед ответом движка, мс (UX: ход не «телепортируется»). */
export const ENGINE_DELAY_MS = 400;

export type PlayResult = 'checkmate' | 'stalemate' | 'draw' | null;

export type AnalyzeFn = (fen: string) => Promise<EngineAnalysis>;

/** Итог позиции: мат/пат/ничья или null, если игра продолжается. */
export function gameStatus(fen: string): PlayResult {
  try {
    const c = new Chess();
    c.load(fen, { skipValidation: true });
    if (c.isCheckmate()) return 'checkmate';
    if (c.isStalemate()) return 'stalemate';
    if (c.isInsufficientMaterial() || c.isThreefoldRepetition() || c.isDraw()) return 'draw';
    return null;
  } catch {
    return null;
  }
}

/** Сторона, чей ход в позиции. */
export function turnOf(fen: string): 'w' | 'b' {
  return fen.trim().split(/\s+/)[1] === 'b' ? 'b' : 'w';
}

export interface EngineMove {
  uci: string;
  san: string;
  fen: string; // позиция ПОСЛЕ хода движка
}

/** Применить UCI-ход к позиции: {san, fen-после} или null, если ход нелегален. */
export function applyUci(fen: string, uci: string | null | undefined): EngineMove | null {
  if (!uci || uci.length < 4) return null;
  try {
    const c = new Chess();
    c.load(fen, { skipValidation: true });
    const m = c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] || undefined });
    if (!m) return null;
    return { uci, san: m.san, fen: c.fen() };
  } catch {
    return null;
  }
}

/**
 * Ход движка из позиции fen: спрашиваем bestmove и применяем его. Возвращает null
 * при сбое анализа, отсутствии bestmove или нелегальном ходе (без исключений).
 */
export async function engineReply(fen: string, analyze: AnalyzeFn): Promise<EngineMove | null> {
  let a: EngineAnalysis;
  try {
    a = await analyze(fen);
  } catch {
    return null;
  }
  return applyUci(fen, a?.bestmove);
}

/** Номер полного хода из FEN (для нумерации записи). */
export function moveNumberOf(fen: string): number {
  const n = Number(fen.trim().split(/\s+/)[5]);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export interface LoopEvents {
  /** Движок сходил — показать ход на доске. beforeFen = позиция ДО хода (для записи). */
  onEngineMove(m: EngineMove, beforeFen: string): void;
  /** Свежая оценка позиции (cp со стороны белых, null = неизвестно). */
  onEval(cpWhite: number | null): void;
  onFinish(result: Exclude<PlayResult, null>, by: 'user' | 'engine'): void;
}

/** Запись хода для ленты партии с ботом. */
export interface Recorded {
  white: boolean;
  moveNo: number;
  san: string;
}

/** Кадр ленты партии с ботом (позиция + приведший к ней ход + оценка). */
interface PlayFrame {
  fen: string;
  last: { from: string; to: string } | null;
  rec: Recorded | null; // ход, приведший к кадру (null — старт)
  cpWhite: number | null;
}

/**
 * Чистая машина состояний игры юзер-против-движка (без DOM, покрыта vitest).
 * Жёсткие гейты от зацикливания «движок играет за обе стороны» (live-баг 2026-07-11):
 *  - ход с доски принимается ТОЛЬКО когда в модели очередь юзера И движок не думает —
 *    эхо программного хода движка / ход чужими фигурами отвергается;
 *  - engineMove ходит ТОЛЬКО когда очередь НЕ юзера — повторный/спурьёзный вызов
 *    после ответа молча выходит, не трогая движок.
 */
export class PlayLoop {
  /** Текущая позиция модели (источник правды; доска — лишь отображение). */
  fen: string;
  finished: PlayResult = null;
  /** Ход движка запрошен/выполняется — ввод юзера игнорируется. */
  thinking: boolean;
  private seq = 0; // инвалидация подвисших engineMove после stop()
  private frames: PlayFrame[]; // лента партии: [0] — старт, далее по полуходам
  private redo: PlayFrame[] = []; // откатанные ◀ кадры (чистятся новым ходом юзера)

  constructor(
    private userSide: 'w' | 'b',
    startFen: string,
    private analyze: AnalyzeFn,
    private ev: LoopEvents,
  ) {
    this.fen = startFen;
    this.thinking = turnOf(startFen) !== userSide; // движок открывает — ввод юзера закрыт
    this.frames = [{ fen: startFen, last: null, rec: null, cpWhite: null }];
  }

  /** Ждём ход юзера. */
  get waitingUser(): boolean {
    return !this.finished && !this.thinking && turnOf(this.fen) === this.userSide;
  }

  /** Лента ходов партии с ботом (для рендера записи). */
  get record(): Recorded[] {
    return this.frames.slice(1).map((f) => f.rec!).filter(Boolean) as Recorded[];
  }

  /** Текущий кадр ленты (позиция + маркер + оценка) — для перерисовки доски. */
  get current(): { fen: string; last: { from: string; to: string } | null; cpWhite: number | null } {
    const f = this.frames[this.frames.length - 1]!;
    return { fen: f.fen, last: f.last, cpWhite: f.cpWhite };
  }

  /** Есть куда откатывать ◀ (сделан хотя бы один полуход). */
  get canBack(): boolean {
    return this.frames.length > 1;
  }

  /** Есть откатанные кадры для ▶ redo. */
  get canForward(): boolean {
    return this.redo.length > 0;
  }

  /**
   * Ход с доски. true = принят как ход юзера (вызывающий шедулит engineMove с
   * задержкой, если игра не кончилась). false = отвергнут — доску нужно вернуть
   * к this.fen.
   */
  acceptUserMove(fenAfter: string, san?: string): boolean {
    if (this.finished || this.thinking) return false;
    if (turnOf(this.fen) !== this.userSide) return false; // не очередь юзера
    const before = this.fen;
    this.push(fenAfter, null, san ? { white: turnOf(before) === 'w', moveNo: moveNumberOf(before), san } : null, null);
    this.redo = []; // новый ход юзера — ветка redo обрезается
    const res = gameStatus(fenAfter);
    if (res) {
      this.finished = res;
      this.ev.onFinish(res, 'user');
      return true;
    }
    this.thinking = true; // до ответа движка новые ходы не принимаем
    return true;
  }

  /** Ровно один ответ движка на текущую позицию (гейты — в докблоке класса). */
  async engineMove(): Promise<void> {
    if (this.finished) return;
    if (turnOf(this.fen) === this.userSide) {
      this.thinking = false;
      return; // очередь юзера — движку отвечать нечего
    }
    const fen = this.fen;
    const seq = ++this.seq;
    let a: EngineAnalysis;
    try {
      a = await this.analyze(fen);
    } catch {
      if (seq === this.seq) this.thinking = false;
      return;
    }
    if (seq !== this.seq || this.fen !== fen || this.finished) return;
    const cpWhite = cpWhiteFromAnalysis(fen, a);
    this.ev.onEval(cpWhite);
    const applied = applyUci(fen, a?.bestmove);
    if (!applied) {
      // движок не дал легального хода — вероятно, позиция закончена
      const res = gameStatus(fen);
      if (res) {
        this.finished = res;
        this.ev.onFinish(res, 'user');
      }
      this.thinking = false;
      return;
    }
    this.push(
      applied.fen,
      { from: applied.uci.slice(0, 2), to: applied.uci.slice(2, 4) },
      { white: turnOf(fen) === 'w', moveNo: moveNumberOf(fen), san: applied.san },
      cpWhite,
    );
    this.thinking = false;
    this.ev.onEngineMove(applied, fen);
    const res = gameStatus(applied.fen);
    if (res) {
      this.finished = res;
      this.ev.onFinish(res, 'engine');
    }
  }

  private push(fen: string, last: PlayFrame['last'], rec: Recorded | null, cpWhite: number | null): void {
    this.frames.push({ fen, last, rec, cpWhite });
    this.fen = fen;
  }

  private popToRedo(): void {
    const f = this.frames.pop();
    if (!f) return;
    this.redo.push(f);
    this.fen = this.frames[this.frames.length - 1]!.fen;
  }

  private redoOne(): void {
    const f = this.redo.pop();
    if (!f) return;
    this.frames.push(f);
    this.fen = f.fen;
    this.finished = gameStatus(f.fen);
  }

  /** ◀ откат: пара полуходов до хода юзера (при думающем боте — отмена+1). */
  takeBack(): void {
    this.stop();
    this.finished = null;
    if (this.frames.length <= 1) return;
    this.popToRedo();
    while (this.frames.length > 1 && turnOf(this.fen) !== this.userSide) this.popToRedo();
  }

  /** ⏮ в начало партии с ботом. */
  toStart(): void {
    this.stop();
    this.finished = null;
    while (this.frames.length > 1) this.popToRedo();
  }

  /** ▶ redo: вернуть откатанное до следующей очереди юзера (или до конца). */
  forward(): void {
    if (!this.redo.length) return;
    this.redoOne();
    while (this.redo.length && turnOf(this.fen) !== this.userSide) this.redoOne();
  }

  /** ⏭ redo до конца ленты. */
  toLatest(): void {
    while (this.redo.length) this.redoOne();
  }

  /**
   * После навигации: если очередь бота и партия не кончилась — пометить «думает»
   * и вернуть true (хост шедулит engineMove — бот доигрывает с текущего кадра).
   */
  resume(): boolean {
    if (!this.finished && turnOf(this.fen) !== this.userSide) {
      this.thinking = true;
      return true;
    }
    this.thinking = false;
    return false;
  }

  /** Остановить: подвисшие engineMove после этого ничего не меняют. */
  stop(): void {
    this.seq++;
    this.thinking = false;
  }
}

export interface PlayHintEvents {
  /** Показать/убрать стрелку лучшего хода (null — убрать). */
  onArrow(a: { from: string; to: string } | null): void;
  /** 💡 считает (true) / готово (false) — кнопка в состоянии «думает». */
  onBusy(busy: boolean): void;
}

/**
 * Подсказка «лучший ход» ВО ВРЕМЯ игры с движком (💡 в режиме play). Чистая
 * машина состояний (без DOM, покрыта vitest): тап → on-demand анализ текущей
 * позиции через инжектированный analyze (обычно sched.requestPriority с ON_DEMAND)
 * → стрелка bestmove. Повторный тап прячет стрелку. Смена позиции (ход юзера/бота)
 * или выход инвалидируют запрос через seq — подвисший ответ на старую позицию
 * стрелку не рисует. На серию задач не влияет (состояние отдельное).
 */
export class PlayHint {
  private fen: string | null = null; // позиция, для которой показана стрелка
  private busy = false;
  private seq = 0;

  constructor(
    private analyze: AnalyzeFn,
    private ev: PlayHintEvents,
  ) {}

  /** Стрелка сейчас показана. */
  get shown(): boolean {
    return this.fen != null;
  }

  /** Идёт расчёт (кнопка «думает»). */
  get pending(): boolean {
    return this.busy;
  }

  /** Тап по 💡 для позиции fen: показать стрелку или (если уже показана) спрятать. */
  async toggle(fen: string): Promise<void> {
    if (this.busy) return; // уже считаем — игнорируем повторный тап
    if (this.fen != null) {
      this.hide();
      return;
    }
    const seq = ++this.seq;
    this.busy = true;
    this.ev.onBusy(true);
    let uci: string | undefined;
    try {
      uci = (await this.analyze(fen))?.bestmove ?? undefined;
    } catch {
      /* superseded / сбой движка — молча */
    }
    this.busy = false;
    this.ev.onBusy(false);
    if (seq !== this.seq) return; // позиция сменилась/сброшено, пока считали
    if (uci && uci.length >= 4) {
      this.fen = fen;
      this.ev.onArrow({ from: uci.slice(0, 2), to: uci.slice(2, 4) });
    }
  }

  /**
   * Привести стрелку к состоянию тумблера 💡: want=false — убрать; want=true —
   * показать bestmove для позиции fen (пересчитать, если позиция сменилась).
   * Идемпотентно для одной и той же позиции. Устаревшие ответы гасятся через seq.
   */
  async sync(fen: string, want: boolean): Promise<void> {
    if (!want) {
      this.hide();
      return;
    }
    if (this.fen === fen && !this.busy) return; // уже показана для этой позиции
    const seq = ++this.seq;
    this.busy = true;
    this.ev.onBusy(true);
    let uci: string | undefined;
    try {
      uci = (await this.analyze(fen))?.bestmove ?? undefined;
    } catch {
      /* superseded / сбой движка — молча */
    }
    this.busy = false;
    this.ev.onBusy(false);
    if (seq !== this.seq) return; // позиция сменилась/сброшено, пока считали
    if (uci && uci.length >= 4) {
      this.fen = fen;
      this.ev.onArrow({ from: uci.slice(0, 2), to: uci.slice(2, 4) });
    } else {
      this.fen = null;
      this.ev.onArrow(null);
    }
  }

  /** Снять стрелку и инвалидировать текущий запрос (смена позиции / выход). */
  hide(): void {
    this.seq++;
    if (this.fen != null) {
      this.fen = null;
      this.ev.onArrow(null);
    }
  }
}
